import { useState, type ReactNode } from 'react';
import { Box, Typography } from '@mui/material';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import type { IInfluencer, IPriceCard } from '../../../redux/buzzbridge/types';
import type { RateCardPlatformId } from '../utils/rateCardPlatformConfig';
import { buildJobsTabRows, filterRequestsTabRows } from '../utils/partitionBuzzBridgeParticipations';
import { ProfileHeader } from './ProfileHeader';
import { BuzzBridgeProfileIncompleteBanner } from './BuzzBridgeProfileIncompleteBanner';
import { CreatorSocialPills } from './CreatorSocialPills';
import { CreatorStatusStrip } from './CreatorStatusStrip';
import { CreatorFinanceRail } from './CreatorFinanceRail';
import { WorkManagementSection } from './WorkManagementSection';
import { CreatorCommissionEarningsSection } from '../CreatorCommissionEarningsSection';
import { LoyaltyReferralEntry } from '../Loyalty/LoyaltyReferralEntry';
import { InfluencerRateCardShareEntry } from './InfluencerRateCardShareEntry';
import { RateCardBuilderModal } from './RateCardBuilderModal';

const GOLD = '#ffc965';
const MINT = '#00fdc1';
const ON_VARIANT = '#adaaaa';
const ON_PRIMARY = '#392700';

export interface CreatorWorkspaceProps {
    influencer: IInfluencer;
    invitationCount: number;
    hasPerformance: boolean;
    workspaceView: 'campaigns' | 'performance';
    onWorkspaceView: (view: 'campaigns' | 'performance') => void;
    activeTab: string;
    tabOptions: { label: string; value: string }[];
    onTabChange: (value: string) => void;
    onEditProfile: (tab?: number) => void;
    onCashOut: () => void;
    onOpenCampaign: (campaignId: string, participantId?: string) => void;
    onSaveRateCard: (platform: RateCardPlatformId, pricing: Partial<IPriceCard>) => void;
    savingRateCard?: boolean;
    tabContent: ReactNode;
}

export function CreatorWorkspace({
    influencer,
    invitationCount,
    hasPerformance,
    workspaceView,
    onWorkspaceView,
    activeTab,
    tabOptions,
    onTabChange,
    onEditProfile,
    onCashOut,
    onOpenCampaign,
    onSaveRateCard,
    savingRateCard,
    tabContent,
}: CreatorWorkspaceProps) {
    const [rateCardPlatform, setRateCardPlatform] = useState<RateCardPlatformId>('instagram');
    const [rateCardOpen, setRateCardOpen] = useState(false);
    const showPerformance = hasPerformance && workspaceView === 'performance';
    const counts = {
        jobs: buildJobsTabRows(influencer.active_jobs, influencer.requests).length,
        requests: filterRequestsTabRows(influencer.requests).length,
        invitations: invitationCount,
        completed: influencer.completed_jobs_count ?? influencer.completed_jobs?.length ?? 0,
    };

    return (
        <>
            <ProfileHeader influencer={influencer} onEdit={() => onEditProfile(0)} />
            <BuzzBridgeProfileIncompleteBanner influencer={influencer} onCompleteProfile={() => onEditProfile(0)} />
            <CreatorSocialPills
                influencer={influencer}
                onLinkAccount={() => onEditProfile(5)}
                onPlatformClick={platform => {
                    setRateCardPlatform(platform);
                    setRateCardOpen(true);
                }}
            />
            <CreatorStatusStrip
                activeJobs={influencer.active_jobs_count ?? counts.jobs}
                pendingReview={influencer.pending_applications_count ?? 0}
                completed={counts.completed}
                invitations={counts.invitations}
            />
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 8fr) minmax(280px, 4fr)' },
                    gap: 3,
                    alignItems: 'start',
                }}>
                <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                    {hasPerformance ? (
                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                                p: 0.5,
                                width: 'fit-content',
                                maxWidth: '100%',
                                borderRadius: 999,
                                bgcolor: '#131313',
                                border: '1px solid #262626',
                            }}>
                            {(
                                [
                                    { value: 'campaigns' as const, label: 'Active campaigns', count: counts.jobs },
                                    { value: 'performance' as const, label: 'Performance', live: true },
                                ] as const
                            ).map(option => {
                                const selected = workspaceView === option.value;
                                return (
                                    <Box
                                        key={option.value}
                                        component="button"
                                        type="button"
                                        onClick={() => onWorkspaceView(option.value)}
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 0.75,
                                            px: 1.75,
                                            py: 0.75,
                                            border: 'none',
                                            borderRadius: 999,
                                            cursor: 'pointer',
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontSize: 12,
                                            fontWeight: 700,
                                            color: selected ? ON_PRIMARY : ON_VARIANT,
                                            bgcolor: selected ? GOLD : 'transparent',
                                            boxShadow: selected ? '0 1px 2px rgba(0,0,0,0.2)' : 'none',
                                            '&:hover': {
                                                bgcolor: selected ? GOLD : '#1a1919',
                                                color: selected ? ON_PRIMARY : '#fff',
                                            },
                                        }}>
                                        <Box
                                            sx={{
                                                width: 8,
                                                height: 8,
                                                borderRadius: '50%',
                                                bgcolor:
                                                    option.value === 'performance'
                                                        ? MINT
                                                        : selected
                                                        ? ON_PRIMARY
                                                        : ON_VARIANT,
                                                boxShadow: option.value === 'performance' ? '0 0 6px #00fdc1' : 'none',
                                            }}
                                        />
                                        {option.label}
                                        {'count' in option ? (
                                            <Box
                                                component="span"
                                                sx={{
                                                    px: 0.75,
                                                    borderRadius: 999,
                                                    bgcolor: '#000',
                                                    color: GOLD,
                                                    fontSize: 10,
                                                    lineHeight: 1.4,
                                                }}>
                                                {option.count}
                                            </Box>
                                        ) : (
                                            <Box
                                                component="span"
                                                sx={{
                                                    px: 0.75,
                                                    borderRadius: 999,
                                                    bgcolor: 'rgba(0,253,193,0.1)',
                                                    color: MINT,
                                                    fontSize: 10,
                                                    lineHeight: 1.4,
                                                }}>
                                                Live
                                            </Box>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>
                    ) : null}
                    {showPerformance ? (
                        <CreatorCommissionEarningsSection embedded onOpenCampaign={onOpenCampaign} />
                    ) : (
                        <>
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontSize: 11,
                                            letterSpacing: '0.14em',
                                            textTransform: 'uppercase',
                                            color: GOLD,
                                            lineHeight: 1,
                                        }}>
                                        Workflow Hub
                                    </Typography>
                                    <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: GOLD }} />
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            fontSize: 12,
                                            color: ON_VARIANT,
                                            lineHeight: 1,
                                        }}>
                                        BuzzBridge Pipeline
                                    </Typography>
                                </Box>
                                <Typography
                                    component="h2"
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontWeight: 800,
                                        fontSize: { xs: 18, md: 20 },
                                        color: '#fff',
                                        mt: 0.5,
                                        lineHeight: 1.2,
                                    }}>
                                    Your Active Work & Pipeline
                                </Typography>
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        fontSize: 12,
                                        color: ON_VARIANT,
                                        mt: 0.25,
                                        lineHeight: 1.4,
                                    }}>
                                    Track current productions, respond to inbound brand requests, and manage deliveries
                                    in one place.
                                </Typography>
                            </Box>
                            <WorkManagementSection
                                activeTab={activeTab}
                                tabOptions={tabOptions}
                                counts={counts}
                                onTabChange={onTabChange}>
                                {tabContent}
                            </WorkManagementSection>
                        </>
                    )}
                </Box>
                <CreatorFinanceRail influencer={influencer} onCashOut={onCashOut} />
            </Box>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    gap: 2,
                    alignItems: 'stretch',
                }}>
                <LoyaltyReferralEntry />
                <InfluencerRateCardShareEntry
                    influencer={influencer}
                    onSaveRateCard={onSaveRateCard}
                    savingRateCard={savingRateCard}
                />
            </Box>
            <RateCardBuilderModal
                open={rateCardOpen}
                onClose={() => setRateCardOpen(false)}
                influencer={influencer}
                initialPlatform={rateCardPlatform}
                saving={savingRateCard}
                onSave={(platform, pricing) => {
                    onSaveRateCard(platform, pricing);
                    setRateCardOpen(false);
                }}
            />
        </>
    );
}
